import { Icon } from "~/react/ui/icon"
import { Tag, TagIcon, TagLabel } from "~/react/ui/tag"
import { Tooltip, TooltipContent, TooltipTrigger } from "~/react/ui/tooltip"
import { Checkbox } from "~/react/ui/checkbox"
import type { DiscoverDomain } from "~/react/types/discover"
import { getCompanyLogoUrl } from "~/react/components/discover_filters/domain-helpers"
import defaultCompanyLogoUrl from "~/images/default_company_logo.svg"

const t = (key: string, options?: Record<string, unknown>) => window.i18n.t(key, options)

interface DiscoverResultsRowProps {
  domain: DiscoverDomain
  hasRecentFunding: boolean
  isHiring: boolean
  onClick: () => void
}

export function DiscoverResultsRow({ domain, hasRecentFunding, isHiring, onClick }: DiscoverResultsRowProps) {
  const locationParts = [domain.city, domain.stateName, domain.countryName].filter(Boolean)
  const locationTooltip = locationParts.join(", ")
  const displayLocation = domain.city || domain.stateName || domain.countryName

  const truncatedIndustry =
    domain.industryName && domain.industryName.length > 30
      ? domain.industryName.slice(0, 27) + "..."
      : domain.industryName

  const companyName = domain.companyName || domain.value

  return (
    <tr className="h-table__row-link" onClick={onClick}>
      <td className="h-table__checkbox discover-results-row__checkbox">
        <Checkbox
          largeClickArea
          aria-label={t("js.discover.index.select_company", {
            name: companyName,
          })}
          data-block-follow="true"
          data-domain-id={domain.id}
          data-domain-name={domain.value}
          data-company-name={companyName}
          data-emails-count={domain.emailsCount}
          data-check-all-target="check"
        />
      </td>
      <td className="companies-table__company discover-results-row__company">
        <div className="h-table-company">
          <img
            className="h-table-company__logo"
            src={getCompanyLogoUrl(domain.value)}
            alt=""
            onError={(e) => {
              e.currentTarget.src = defaultCompanyLogoUrl
            }}
          />
          <div className="h-table-company__infos">
            <div className="h-table-company__heading">
              {domain.companyName ? (
                <>
                  <strong className="h-table-company__name">{domain.companyName}</strong>
                  <span className="h-table-company__domain">{domain.value}</span>
                  {/* TODO(dev:discover_v2): This should check if the company is saved, not rely on isHiring */}
                  {isHiring && (
                    <span className="h-table-company__domain h-table-company__domain-saved">
                      {" · "}
                      {t("js.discover.tags.saved")}
                    </span>
                  )}
                </>
              ) : (
                <strong className="h-table-company__name">{domain.value}</strong>
              )}
            </div>
            <ul className="h-table-company__metas">
              {domain.size && (
                <li className="h-table-company__meta">
                  <Icon icon="users" className="h-table-company__meta-icon" />
                  {domain.size}
                </li>
              )}
              {domain.industryName && (
                <li className="h-table-company__meta">
                  <Icon icon="buildings" className="h-table-company__meta-icon" />
                  {domain.industryName.length > 30 ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>{truncatedIndustry}</span>
                      </TooltipTrigger>
                      <TooltipContent>{domain.industryName}</TooltipContent>
                    </Tooltip>
                  ) : (
                    domain.industryName
                  )}
                </li>
              )}
              {displayLocation && (
                <li className="h-table-company__meta">
                  <Icon icon="map-location-dot" className="h-table-company__meta-icon" />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>{displayLocation}</span>
                    </TooltipTrigger>
                    <TooltipContent>{locationTooltip}</TooltipContent>
                  </Tooltip>
                </li>
              )}
            </ul>
          </div>
        </div>
      </td>
      <td className="discover-results-row__tags">
        <Tag>
          <TagLabel>
            {t("js.discover.tags.emails", {
              count: domain.emailsCount,
              emails: domain.emailsCount.toLocaleString(),
            })}
          </TagLabel>
        </Tag>
        {hasRecentFunding && (
          <Tag variant="ai">
            <TagIcon>
              <Icon icon="sparkle" />
            </TagIcon>
            <TagLabel>{t("js.discover.tags.recently_raised_funds")}</TagLabel>
          </Tag>
        )}
        {isHiring && (
          <Tag variant="ai">
            <TagIcon>
              <Icon icon="sparkle" />
            </TagIcon>
            <TagLabel>{t("js.discover.tags.currently_hiring")}</TagLabel>
          </Tag>
        )}
      </td>
    </tr>
  )
}
